import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Lock } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { useExamInsights } from '../context';

interface LockedInsightProps {
  /** What is behind the lock, e.g. "Why this answer is right". */
  title: string;
  /** One honest line on what unlocking shows. */
  description: string;
  /** Placeholder line count: the shape of the hidden content, not the content. */
  lines?: number;
  className?: string;
}

/* Fixed widths so the placeholder looks like prose and never changes between renders. */
const LINE_WIDTHS = ['92%', '78%', '86%', '64%', '81%', '70%'];

/**
 * A locked part of an IELTS/SAT review. The blurred lines are a placeholder: the API sends nothing to blur,
 * so there is nothing here to read in the browser's tools.
 */
export function LockedInsight({ title, description, lines = 3, className }: LockedInsightProps) {
  const { t } = useTranslation();
  const { requestUnlock, unlocking } = useExamInsights();

  return (
    <section
      aria-label={title}
      className={cn(
        'relative mt-5 overflow-hidden rounded-xl border border-dashed border-gray-300 bg-white dark:border-white/15 dark:bg-white/5',
        className,
      )}
    >
      <div aria-hidden className="pointer-events-none select-none space-y-2.5 p-4 blur-[3px]">
        {LINE_WIDTHS.slice(0, Math.max(1, Math.min(lines, LINE_WIDTHS.length))).map((width, i) => (
          <div key={i} className="h-2.5 rounded-full bg-gray-200 dark:bg-white/15" style={{ width }} />
        ))}
      </div>
      <div className="absolute inset-0 flex flex-col items-start justify-center gap-2 bg-gradient-to-r from-white via-white/95 to-white/60 p-4 dark:from-gray-950 dark:via-gray-950/95 dark:to-gray-950/60 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-900 text-white dark:bg-white dark:text-gray-900">
            <Lock className="h-3.5 w-3.5" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{title}</p>
            <p className="text-[13px] leading-snug text-muted-foreground">{description}</p>
          </div>
        </div>
        <Button type="button" size="sm" className="shrink-0" onClick={requestUnlock} disabled={unlocking}>
          {unlocking && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden />}
          {t('exam_insights.unlock')}
        </Button>
      </div>
    </section>
  );
}
